import { createAsyncThunk } from "@reduxjs/toolkit";
import { getPresignedUrl, uploadFile } from "./new-booking-form.slice";
import { compressImageAPI, compressImageClientSide } from "../../services/compressionService";
import { getCompressionDimensions, isNotImageFile, getUploadSuccessMessage } from "../../utilis/uploadUtils";

// Helper function to convert base64 to File
function base64ToFile(base64, fileName, mimeType) {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ia], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

export const compressImage = createAsyncThunk(
  'compressImage',
  async (payload, thunkAPI) => {
    try {
      const { file, width = 800, height = 600 } = payload;
      
      // Try server-side compression first
      try {
        const result = await compressImageAPI(file, width, height);
        if (result.success) {
          return {
            data: result.data,
            filename: result.filename,
          
          };
        }
      } catch (serverError) {
        console.warn('Server-side compression failed, falling back to client-side:', serverError.message);
        
        // Fallback to client-side compression
        const result = await compressImageClientSide(file, width, height, 0.8);
        if (result.success) {
          return {
            data: result.data,
            filename: result.filename,

          };
        }
      }
      
      throw new Error('Both server-side and client-side compression failed');
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || 'Image compression failed');
    }
  }
);

export const compressAndUploadFile = createAsyncThunk(
  'compressAndUploadFile',
  async (payload, thunkAPI) => {
    try {
      const { file, presignedUrlPayload, fieldName } = payload;
      const isNonImageFile = isNotImageFile(file);
      let compressedImageData = null;
      let fileToUpload = file;
      let presignedPayload = presignedUrlPayload;

      if (!isNonImageFile) {
        // Get dynamic compression dimensions based on field name
        const dimensions = getCompressionDimensions(fieldName || '');
        
        // Step 1: Compress image
        const compressResult = await thunkAPI.dispatch(
          compressImage({
            file,
            width: payload.width || dimensions.width,
            height: payload.height || dimensions.height,
          })
        );

        if (compressImage.rejected.match(compressResult)) {
          return thunkAPI.rejectWithValue('Image compression failed');
        }

        const { data: base64Str, filename: compressedKeyFromAPI } = compressResult.payload || {};

        if (!base64Str || !compressedKeyFromAPI) {
          return thunkAPI.rejectWithValue('Invalid compression result');
        }

        // Convert back to File
        const mimeType = 'image/jpeg';
        const dataUrl = `data:${mimeType};base64,${base64Str}`;
        const compressedFile = base64ToFile(dataUrl, file?.name, mimeType);

        compressedImageData = {
          base64: base64Str,
          compressedFile,
          originalSize: file.size,
          compressedSize: compressedFile.size,
          dimensions,
        };

        fileToUpload = compressedFile;

        // Override key from compression result
        presignedPayload = {
          ...presignedPayload,
          key: compressedKeyFromAPI,
        };
      }

      // 🔹 Step 2: Get presigned URL (only reached if compression succeeded for images,
      // or if it's a non-image file)
      const presignedResult = await thunkAPI.dispatch(getPresignedUrl(presignedPayload));

      if (getPresignedUrl.rejected.match(presignedResult)) {
        return thunkAPI.rejectWithValue('Failed to get presigned URL');
      }

      const presignedResponse = presignedResult?.payload;

      if (!presignedResponse || presignedResponse.statusCode !== 201) {
        return thunkAPI.rejectWithValue('Failed to get valid presigned URL');
      }

      const presignedUrl = presignedResponse.data?.signedUrl;

      if (!presignedUrl) {
        return thunkAPI.rejectWithValue('No presigned URL received');
      }

      // 🔹 Step 3: Upload
      const uploadResult = await thunkAPI.dispatch(
        uploadFile({
          presignedUrl,
          file: fileToUpload,
        })
      );

      if (uploadFile.rejected.match(uploadResult)) {
        return thunkAPI.rejectWithValue('File upload failed');
      }

      return {
        compressionResult: compressedImageData,
        presignedResponse,
        uploadResult: uploadResult?.payload,
        isNonImageFile,
        successMessage: getUploadSuccessMessage(file),
        fileType: file.type,
        originalFileName: file.name,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message || 'Upload process failed');
    }
  }
);
